import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { api } from '../../utils/api';
import ProtectedRoute from '../../components/ProtectedRoute';
import AdminLayout, { useTheme } from '../../components/AdminLayout';
import NumberInput from '../../components/NumberInput';
import { useNotification } from '../../contexts/NotificationContext';

const DURATION_OPTIONS = [15, 30, 45, 60, 75, 90, 105, 120];

function ServicesContent() {
  const { t } = useTranslation();
  const { showAlert, showConfirm } = useNotification();
  const { T, S } = useTheme();
  const { user } = useAuth();
  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', duration_minutes: 30, price: '', category_id: '' });
  const [categoryModal, setCategoryModal] = useState(null);
  const [categoryForm, setCategoryForm] = useState({ name: '' });

  const shopId = user?.shop_id;
  const branchId = user?.branch_id;
  const [selectedBranchId, setSelectedBranchId] = useState(branchId || null);
  const [branches, setBranches] = useState([]);
  const targetBranchId = selectedBranchId || branchId;
  const categoriesForCurrentBranch = categories.filter(c => c.branch_id === targetBranchId);

  const fetchBranches = async () => {
    if (shopId) {
      try {
        const data = await api.getManagerBranches();
        setBranches(data || []);
        if (data?.length > 0 && !selectedBranchId && !branchId) setSelectedBranchId(data[0].id);
        else if (branchId && !selectedBranchId) setSelectedBranchId(branchId);
      } catch (err) { console.error(err); }
    }
  };

  const fetchServices = async () => {
    if (!selectedBranchId && !branchId) return;
    try { const data = await api.getManagerServices(); setServices(data); }
    catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const fetchCategories = async () => {
    try { const data = await api.getManagerCategories(targetBranchId || undefined); setCategories(data || []); }
    catch (err) { console.error(err); }
  };

  useEffect(() => { if (shopId || branchId) fetchBranches(); }, [shopId, branchId]);
  useEffect(() => { fetchServices(); }, [selectedBranchId]);
  useEffect(() => { if (targetBranchId) fetchCategories(); }, [targetBranchId]);

  const openCreate = () => {
    if (!selectedBranchId && !branchId) { showAlert(t('admin.pleaseSelectBranch')); return; }
    setEditing('create'); setForm({ name: '', duration_minutes: 30, price: '', category_id: '' });
  };
  const openEdit = (s) => { setEditing(s.id); setForm({ name: s.name, duration_minutes: s.duration_minutes, price: s.price, category_id: s.category_id ?? '' }); };

  const save = async () => {
    if (!targetBranchId) { showAlert(t('admin.branchAndNameRequired')); return; }
    try {
      const payload = {
        branch_id: targetBranchId,
        name: form.name,
        duration_minutes: Number(form.duration_minutes) || 30,
        price: parseFloat(form.price),
        category_id: form.category_id ? parseInt(form.category_id) : null,
      };
      if (editing === 'create') await api.createManagerService(payload);
      else await api.updateManagerService(editing, { name: form.name, duration_minutes: payload.duration_minutes, price: payload.price, category_id: payload.category_id });
      setEditing(null); fetchServices(); fetchCategories();
    } catch (err) { showAlert(err.message); }
  };

  const saveCategory = async () => {
    if (!categoryForm.name?.trim() || !targetBranchId) { showAlert(t('manager.nameRequired')); return; }
    try {
      if (categoryModal?.id) await api.updateManagerCategory(categoryModal.id, { name: categoryForm.name.trim() });
      else await api.createManagerCategory({ branch_id: targetBranchId, name: categoryForm.name.trim() });
      setCategoryModal(null); fetchCategories();
    } catch (err) { showAlert(err.message); }
  };

  const deleteCategory = async (id) => {
    showConfirm(t('admin.deleteCategoryConfirm'), async () => {
      try { await api.deleteManagerCategory(id); fetchCategories(); fetchServices(); } catch (err) { showAlert(err.message); }
    });
  };

  const remove = async (id) => {
    showConfirm(t('manager.deleteServiceConfirm'), async () => {
      try { await api.deleteManagerService(id); fetchServices(); }
      catch (err) { showAlert(err.message); }
    });
  };

  return (
    <AdminLayout role="manager">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '2rem' }}>
        <div>
          <h1 style={S.pageTitle}>{t('admin.services')}</h1>
          <div style={{ width: '40px', height: '2px', background: T.orange }} />
        </div>
        {(selectedBranchId || branchId) && (
          <button style={S.btnOrange} onClick={openCreate}
            onMouseEnter={e => e.target.style.background = T.orangeHov}
            onMouseLeave={e => e.target.style.background = T.orange}>+ {t('admin.addService')}</button>
        )}
      </div>

      {(shopId && branches.length > 0) || branchId ? (
        <div style={{ marginBottom: '1.5rem' }}>
          <label style={{ display: 'block', fontSize: '0.78rem', color: T.muted, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '0.4rem' }}>{t('admin.selectBranch')}</label>
          <select value={selectedBranchId || ''} onChange={e => setSelectedBranchId(parseInt(e.target.value) || null)} style={{ ...S.select, width: 'auto', marginBottom: 0 }}>
            <option value="">{t('admin.chooseBranch')}</option>
            {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        </div>
      ) : null}

      {/* Categories for current branch */}
      {targetBranchId && (
        <div style={{ marginBottom: '1.5rem' }}>
          <h2 style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '1rem', fontWeight: 700, letterSpacing: '0.08em', color: T.white, marginBottom: '0.75rem' }}>{t('admin.categories')}</h2>
          <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
            <button style={S.btnOrange} onClick={() => { setCategoryModal('create'); setCategoryForm({ name: '' }); }}
              onMouseEnter={e => e.target.style.background = T.orangeHov}
              onMouseLeave={e => e.target.style.background = T.orange}>+ {t('admin.addCategory')}</button>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={S.table}>
              <thead><tr>{[t('admin.id'), t('admin.name'), t('admin.actions')].map((h, i) => <th key={i} style={S.th}>{h}</th>)}</tr></thead>
              <tbody>
                {categoriesForCurrentBranch.map(c => (
                  <tr key={c.id} onMouseEnter={e => e.currentTarget.style.background = 'rgba(27,213,173,0.04)'} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    <td style={S.td}>{c.id}</td>
                    <td style={S.td}>{c.name}</td>
                    <td style={S.td}>
                      <button style={S.btnSmall} onClick={() => { setCategoryModal(c); setCategoryForm({ name: c.name }); }}>{t('common.edit')}</button>
                      <button style={S.btnDanger} onClick={() => deleteCategory(c.id)}>{t('common.delete')}</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {categoryModal && (
            <div style={S.modal}>
              <div style={S.modalBox}>
                <h2 style={S.modalTitle}>{categoryModal === 'create' ? t('admin.newCategory') : t('admin.editCategory')}</h2>
                <input placeholder={t('admin.categoryName')} value={categoryForm.name} onChange={e => setCategoryForm({ name: e.target.value })} style={S.input} />
                <div style={{ display: 'flex', gap: '0.75rem' }}>
                  <button onClick={saveCategory} style={{ ...S.btnOrange, flex: 1 }}>{t('common.save')}</button>
                  <button onClick={() => setCategoryModal(null)} style={S.btnOutline}>{t('common.cancel')}</button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {editing && (
        <div style={S.modal}>
          <div style={S.modalBox}>
            <h2 style={S.modalTitle}>{editing === 'create' ? t('admin.newService') : t('admin.editService')}</h2>
            <input placeholder={t('admin.name')} value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} style={S.input} />
            <label style={{ display: 'block', fontSize: '0.78rem', color: T.muted, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '0.4rem' }}>{t('admin.durationSteps')}</label>
            <select value={form.duration_minutes} onChange={e => setForm({ ...form, duration_minutes: Number(e.target.value) })} style={S.select}>
              {DURATION_OPTIONS.map(m => <option key={m} value={m}>{m} {t('admin.min')}</option>)}
            </select>
            <NumberInput placeholder={t('admin.priceEgp')} value={form.price} onChange={(v) => setForm({ ...form, price: v })} inputStyle={S.input} min={0} step={0.01} />
            <label style={{ display: 'block', fontSize: '0.78rem', color: T.muted, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '0.4rem' }}>{t('admin.categoryOptional')}</label>
            <select value={form.category_id} onChange={e => setForm({ ...form, category_id: e.target.value })} style={S.select}>
              <option value="">{t('admin.none')}</option>
              {categoriesForCurrentBranch.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button onClick={save} style={{ ...S.btnOrange, flex: 1 }}>{t('common.save')}</button>
              <button onClick={() => setEditing(null)} style={S.btnOutline}>{t('common.cancel')}</button>
            </div>
          </div>
        </div>
      )}

      {loading ? <p style={{ color: T.muted }}>{t('common.loading')}</p> : (
        <div style={{ overflowX: 'auto' }}>
          <table style={S.table}>
            <thead>
              <tr>{[t('admin.id'), t('admin.name'), t('admin.category'), t('admin.duration'), t('admin.price'), t('admin.actions')].map((h, i) => <th key={i} style={S.th}>{h}</th>)}</tr>
            </thead>
            <tbody>
              {services.filter(s => !targetBranchId || s.branch_id === targetBranchId).map(s => (
                <tr key={s.id}
                  onMouseEnter={e => e.currentTarget.style.background = 'rgba(27,213,173,0.04)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <td style={S.td}>{s.id}</td>
                  <td style={S.td}>{s.name}</td>
                  <td style={S.td}>{s.category_name ?? '—'}</td>
                  <td style={S.td}>{s.duration_minutes} {t('admin.min')}</td>
                  <td style={{ ...S.td, color: T.orange }}>{s.price} EGP</td>
                  <td style={S.td}>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button style={S.btnSmall} onClick={() => openEdit(s)}
                        onMouseEnter={e => e.target.style.color = T.white}
                        onMouseLeave={e => e.target.style.color = T.muted}>{t('common.edit')}</button>
                      <button style={S.btnDanger} onClick={() => remove(s.id)}>{t('common.delete')}</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </AdminLayout>
  );
}

export default function Services() {
  return <ProtectedRoute requireSalonManager><ServicesContent /></ProtectedRoute>;
}
